import { useEffect, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { backendUri } from 'helpers/auth/openid/openid-settings.ts';
import {
  EnableSignalRDebug,
  useSignalRConnections,
} from './SignalRConnectionsProvider.tsx';

//--------------------------------------------------------------------------------------------------------------
// <auto-generated>
//     This file is auto-generated by "signal-r-client-generator-script.mjs" script using custom ".hbs" (Handlebars) template.
// </auto-generated>
//--------------------------------------------------------------------------------------------------------------

async function safeInvokeHubMethod(
  connection: signalR.HubConnection | null,
  method: string,
  ...args: any[]
) {
  if (connection?.state !== signalR.HubConnectionState.Connected) {
    if (EnableSignalRDebug)
      console.warn('SignalR not connected — skipping call');
    return;
  }

  try {
    await connection.invoke(method, ...args);
  } catch (err) {
    if (
      err instanceof Error &&
      err.message.includes('underlying connection being closed')
    ) {
      return;
    }
    throw err;
  }
}

//--------------------------------------------------------------------------------------------------------------
// Hubs
//--------------------------------------------------------------------------------------------------------------

export enum SignalRHubs {
  VariableLockHub = 'VariableLockHub',
  EditingLockHub = 'EditingLockHub',
  DocumentLockHub = 'DocumentLockHub',
}

//--------------------------------------------------------------------------------------------------------------
// DTOs
//--------------------------------------------------------------------------------------------------------------

//--------------------------------------------------------------------------------------------------------------
// Event handlers and invokers
//--------------------------------------------------------------------------------------------------------------

export interface IVariableLockClientHandlers {
  VariableLocksChanged(): void;
}

export interface IVariableLockServerMethods {
  SubscribeToVariableLocks(
    variableIds: number[],
    revisionId: number,
  ): Promise<void>;
  UnsubscribeFromVariableLocks(
    variableIds: number[],
    revisionId: number,
  ): Promise<void>;
}

export function useVariableLockHub(
  handlers: Partial<IVariableLockClientHandlers> = {},
  enabled: boolean = true,
): {
  invoker: IVariableLockServerMethods;
  connection: signalR.HubConnection | null;
  isConnected: boolean;
} {
  const { createConnection, setupEventHandlers, deleteConnection } =
    useSignalRConnections();
  const [connection, setConnection] = useState<signalR.HubConnection | null>(
    null,
  );
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (enabled)
      setConnection(
        createConnection(SignalRHubs.VariableLockHub, undefined, setIsConnected)
          .connection,
      );
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !connection) return;

    setupEventHandlers(SignalRHubs.VariableLockHub, handlers);

    return () => {
      deleteConnection(SignalRHubs.VariableLockHub, handlers, setIsConnected);
    };
  }, [backendUri, enabled, connection]);

  const invoker = {
    SubscribeToVariableLocks: async (
      variableIds: number[],
      revisionId: number,
    ) => {
      await safeInvokeHubMethod(
        connection,
        'SubscribeToVariableLocks',
        variableIds,
        revisionId,
      );
    },
    UnsubscribeFromVariableLocks: async (
      variableIds: number[],
      revisionId: number,
    ) => {
      await safeInvokeHubMethod(
        connection,
        'UnsubscribeFromVariableLocks',
        variableIds,
        revisionId,
      );
    },
  };

  return { invoker, connection, isConnected };
}
export interface IEditingLockClientHandlers {
  UnlockDocument(sessionId: string): void;
  DocumentRelocked(initiatorSessionId: string, userName: string): void;
  DocumentContentUpdated(): void;
}

export interface IEditingLockServerMethods {
  JoinDocumentGroup(documentId: number): Promise<void>;
  LeaveDocumentGroup(documentId: number): Promise<void>;
  SubmitDocumentAction(hasActions: boolean, documentId: number): Promise<void>;
}

export function useEditingLockHub(
  handlers: Partial<IEditingLockClientHandlers> = {},
  enabled: boolean = true,
): {
  invoker: IEditingLockServerMethods;
  connection: signalR.HubConnection | null;
  isConnected: boolean;
} {
  const { createConnection, setupEventHandlers, deleteConnection } =
    useSignalRConnections();
  const [connection, setConnection] = useState<signalR.HubConnection | null>(
    null,
  );
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (enabled)
      setConnection(
        createConnection(SignalRHubs.EditingLockHub, undefined, setIsConnected)
          .connection,
      );
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !connection) return;

    setupEventHandlers(SignalRHubs.EditingLockHub, handlers);

    return () => {
      deleteConnection(SignalRHubs.EditingLockHub, handlers, setIsConnected);
    };
  }, [backendUri, enabled, connection]);

  const invoker = {
    JoinDocumentGroup: async (documentId: number) => {
      await safeInvokeHubMethod(connection, 'JoinDocumentGroup', documentId);
    },
    LeaveDocumentGroup: async (documentId: number) => {
      await safeInvokeHubMethod(connection, 'LeaveDocumentGroup', documentId);
    },
    SubmitDocumentAction: async (hasActions: boolean, documentId: number) => {
      await safeInvokeHubMethod(
        connection,
        'SubmitDocumentAction',
        hasActions,
        documentId,
      );
    },
  };

  return { invoker, connection, isConnected };
}
export interface IDocumentLockClientHandlers {
  DocumentLockChanged(documentId: number): void;
}

export interface IDocumentLockServerMethods {
  SubscribeToDocumentLocks(documentIds: number[]): Promise<void>;
  UnsubscribeFromDocumentLocks(documentIds: number[]): Promise<void>;
}

export function useDocumentLockHub(
  handlers: Partial<IDocumentLockClientHandlers> = {},
  enabled: boolean = true,
): {
  invoker: IDocumentLockServerMethods;
  connection: signalR.HubConnection | null;
  isConnected: boolean;
} {
  const { createConnection, setupEventHandlers, deleteConnection } =
    useSignalRConnections();
  const [connection, setConnection] = useState<signalR.HubConnection | null>(
    null,
  );
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (enabled)
      setConnection(
        createConnection(SignalRHubs.DocumentLockHub, undefined, setIsConnected)
          .connection,
      );
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !connection) return;

    setupEventHandlers(SignalRHubs.DocumentLockHub, handlers);

    return () => {
      deleteConnection(SignalRHubs.DocumentLockHub, handlers, setIsConnected);
    };
  }, [backendUri, enabled, connection]);

  const invoker = {
    SubscribeToDocumentLocks: async (documentIds: number[]) => {
      await safeInvokeHubMethod(
        connection,
        'SubscribeToDocumentLocks',
        documentIds,
      );
    },
    UnsubscribeFromDocumentLocks: async (documentIds: number[]) => {
      await safeInvokeHubMethod(
        connection,
        'UnsubscribeFromDocumentLocks',
        documentIds,
      );
    },
  };

  return { invoker, connection, isConnected };
}
